<script setup>
import { computed } from "vue";
import SettingsField from "./SettingsField.vue";
import { GST_TYPES, detailsFromGstin } from "./companySettingsRules";
import { useCompanySettings } from "./useCompanySettings";

const { form, canEdit, update, visibleError } = useCompanySettings();

const gstinDetails = computed(() => (form.GSTIN && form.GSTIN.length === 15 ? detailsFromGstin(form.GSTIN) : null));
const isUnregistered = computed(() => form.GSTType === "UNREGISTERED");
</script>

<template>
  <div class="cs-panel">
    <section class="cs-group">
      <header class="cs-group-head">
        <h5>GST registration</h5>
        <p>Decides how tax is shown on your invoices.</p>
      </header>

      <div class="cs-choice-grid" role="radiogroup" aria-label="GST type">
        <button
          v-for="option in GST_TYPES"
          :key="option.value"
          type="button"
          role="radio"
          class="cs-choice"
          :class="{ active: form.GSTType === option.value }"
          :aria-checked="form.GSTType === option.value"
          :disabled="!canEdit"
          @click="update('GSTType', option.value)"
        >
          <span class="cs-choice-dot" aria-hidden="true"></span>
          <span>
            <strong>{{ option.label }}</strong>
            <small>{{ option.description }}</small>
          </span>
        </button>
      </div>

      <div class="row g-3 mt-1">
        <div class="col-md-6">
          <SettingsField
            field="GSTIN"
            placeholder="32ABCDE1234F1Z5"
            maxlength="15"
            uppercase
            :hint="isUnregistered ? 'Not needed for unregistered businesses.' : 'We fill the state and PAN from it.'"
          >
            <template #after>
              <div v-if="gstinDetails && gstinDetails.state && !visibleError('GSTIN')" class="cs-detected">
                <vue-feather type="check-circle" size="14"></vue-feather>
                {{ gstinDetails.state.name }} ({{ gstinDetails.state.code }})
                <template v-if="gstinDetails.pan"> · PAN {{ gstinDetails.pan }}</template>
              </div>
            </template>
          </SettingsField>
        </div>
        <div class="col-md-6">
          <SettingsField field="PANNo" label="PAN" placeholder="ABCDE1234F" maxlength="10" uppercase />
        </div>
      </div>
    </section>

    <section class="cs-group">
      <header class="cs-group-head">
        <h5>Other licences</h5>
        <p>Only fill what applies to your business.</p>
      </header>
      <div class="row g-3">
        <div class="col-md-6">
          <SettingsField
            field="DrugLicenseNo"
            label="Drug licence number"
            placeholder="e.g. KL-EKM-123456"
            hint="Pharmacies only. Printed on invoices when filled."
            maxlength="100"
          />
        </div>
      </div>
    </section>
  </div>
</template>
